import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridApi, GridOptions } from 'ag-grid-community';
import { AgGridMultiSelectTracker } from '../MultiSelectTracker';

@Component({
    selector: 'ag-grid-row-count',
    templateUrl: './agGridRowCount.component.html',
    styleUrls: ['./agGridRowCount.component.scss'],
    imports: [CommonModule],
    standalone: true,
})
export class AgGridRowCountComponent {
    constructor(private _cdRef: ChangeDetectorRef) {
        const effectRef = effect(() => {
            if (this.gridApi()) {
                this._initializeRowCount();
                effectRef.destroy();
            }
        });
        const effectRef2 = effect(() => {
            if (this.gridTracker()) {
                this._initializeTracker();
                effectRef2.destroy();
            }
        });
    }

    gridTracker = input<AgGridMultiSelectTracker>();
    gridOptions = input<GridOptions>();
    gridApi = input<GridApi>();
    prefix = input<string>('(');
    suffix = input<string>(')');
    cssClass = input<string>('ag-grid-row-count-text');
    useWhiteText = input<boolean>();

    destroyRef = inject(DestroyRef);

    totalRowCount: number = 0;
    selectedRowCount: number = 0;

    supportsSelect = computed(() => {
        return !!(
            this.gridTracker() ||
            this.gridOptions()?.rowSelection === 'single' ||
            this.gridOptions()?.rowSelection === 'multiple'
        );
    });

    private _initializeRowCount(): void {
        const rowDataUpdated = this.gridOptions().onRowDataUpdated;
        this.gridApi().setGridOption('onRowDataUpdated', (params) => {
            if (rowDataUpdated) {
                rowDataUpdated(params);
            }
            this._updateTotalRowCount();
        });

        const filterChanged = this.gridOptions().onFilterChanged;
        this.gridApi().setGridOption('onFilterChanged', (params) => {
            if (filterChanged) {
                filterChanged(params);
            }
            this._updateTotalRowCount();
        });
    }

    private _updateTotalRowCount(): void {
        const gridApi = this.gridApi();
        if (gridApi && this.gridOptions().rowModelType === 'infinite') {
            this.totalRowCount = gridApi.isLastRowIndexKnown() ? gridApi.getDisplayedRowCount() : 0;
        } else {
            this.totalRowCount = gridApi ? gridApi.getDisplayedRowCount() : 0;
        }
        this._cdRef.markForCheck();
    }

    private _initializeTracker(): void {
        this.gridTracker()
            .selectedRows$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.supportsSelect()) {
                    this.selectedRowCount = this.gridTracker().getSelectedRowsCount();
                } else {
                    this.selectedRowCount = this.gridApi().getSelectedRows().length;
                }
            });
    }
}
